import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { TagsInput } from "@/components/TagsInput";

export function ContactCreateModal({ open, onClose, onCreated, initial }) {
  const [form, setForm] = useState({
    first_name: initial?.first_name || "",
    last_name: initial?.last_name || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    address_street: "",
    address_zip: "",
    address_city: "",
    company_name: "",
    vat_number: "",
    notes: "",
    tags: [],
    consent_marketing_granted: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isValid =
    (form.first_name.trim() || form.last_name.trim() || form.email.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setError("Au moins un des champs prénom, nom ou email est requis.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        address_street: form.address_street.trim(),
        address_zip: form.address_zip.trim(),
        address_city: form.address_city.trim(),
        company_name: form.company_name.trim() || null,
        vat_number: form.vat_number.trim().toUpperCase().replace(/[.\s]/g, "") || null,
        notes: form.notes.trim(),
        tags: form.tags,
        consent_marketing_granted: !!form.consent_marketing_granted,
      };
      const { data } = await api.post("/contacts", payload);
      toast.success("Contact créé");
      onCreated?.(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose?.()}>
      <DialogContent
        className="max-w-lg border-slate-800 bg-[#1E1A17] text-slate-100"
        data-testid="contact-create-modal"
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-slate-100">Nouveau contact</DialogTitle>
            <DialogDescription className="text-slate-400">
              Fiche d'identité indépendante. Vous pourrez y attacher des projets ensuite.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cc-first" className="text-xs text-slate-400">Prénom</Label>
                <Input
                  id="cc-first"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="border-slate-700 bg-slate-950/50 text-slate-100"
                  data-testid="cc-first-name"
                />
              </div>
              <div>
                <Label htmlFor="cc-last" className="text-xs text-slate-400">Nom</Label>
                <Input
                  id="cc-last"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="border-slate-700 bg-slate-950/50 text-slate-100"
                  data-testid="cc-last-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="cc-email" className="text-xs text-slate-400">Email</Label>
              <Input
                id="cc-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="border-slate-700 bg-slate-950/50 text-slate-100"
                data-testid="cc-email"
              />
            </div>
            <div>
              <Label htmlFor="cc-phone" className="text-xs text-slate-400">Téléphone (BE)</Label>
              <Input
                id="cc-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="0470 12 34 56"
                className="border-slate-700 bg-slate-950/50 text-slate-100"
                data-testid="cc-phone"
              />
            </div>
            <div>
              <Label htmlFor="cc-street" className="text-xs text-slate-400">Rue et n°</Label>
              <Input
                id="cc-street"
                value={form.address_street}
                onChange={(e) => setForm({ ...form, address_street: e.target.value })}
                className="border-slate-700 bg-slate-950/50 text-slate-100"
                data-testid="cc-street"
              />
            </div>
            <div className="grid grid-cols-[100px_1fr] gap-3">
              <div>
                <Label htmlFor="cc-zip" className="text-xs text-slate-400">CP</Label>
                <Input
                  id="cc-zip"
                  value={form.address_zip}
                  onChange={(e) => setForm({ ...form, address_zip: e.target.value })}
                  className="border-slate-700 bg-slate-950/50 text-slate-100"
                  data-testid="cc-zip"
                />
              </div>
              <div>
                <Label htmlFor="cc-city" className="text-xs text-slate-400">Ville</Label>
                <Input
                  id="cc-city"
                  value={form.address_city}
                  onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                  className="border-slate-700 bg-slate-950/50 text-slate-100"
                  data-testid="cc-city"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cc-company" className="text-xs text-slate-400">Société (optionnel)</Label>
                <Input
                  id="cc-company"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="border-slate-700 bg-slate-950/50 text-slate-100"
                  data-testid="cc-company"
                />
              </div>
              <div>
                <Label htmlFor="cc-vat" className="text-xs text-slate-400">N° TVA (optionnel)</Label>
                <Input
                  id="cc-vat"
                  value={form.vat_number}
                  onChange={(e) => setForm({ ...form, vat_number: e.target.value.toUpperCase() })}
                  placeholder="BE0123456789"
                  className="border-slate-700 bg-slate-950/50 text-slate-100"
                  data-testid="cc-vat"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Tags</Label>
              <TagsInput
                value={form.tags}
                onChange={(tags) => setForm({ ...form, tags })}
                placeholder="Ajouter un tag…"
              />
            </div>
            <div>
              <Label htmlFor="cc-notes" className="text-xs text-slate-400">Notes internes</Label>
              <Textarea
                id="cc-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="border-slate-700 bg-slate-950/50 text-slate-100"
                data-testid="cc-notes"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                checked={form.consent_marketing_granted}
                onChange={(e) => setForm({ ...form, consent_marketing_granted: e.target.checked })}
                className="accent-[#C4553B]"
                data-testid="cc-consent"
              />
              Consentement marketing donné
            </label>

            {error && (
              <div className="rounded border border-red-800/50 bg-red-900/20 p-2 text-xs text-red-200" data-testid="cc-error">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              className="border-slate-700 bg-slate-800/50 text-slate-200"
              data-testid="cc-cancel"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={saving || !isValid}
              className="bg-[#C4553B] text-white hover:bg-[#B44A32]"
              data-testid="cc-submit"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer le contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ContactCreateModal;
